import { useState, useEffect, useCallback, useRef } from 'react';
import { keyframes }  from '@mui/system';
import Box            from '@mui/material/Box';
import Typography     from '@mui/material/Typography';
import Dialog         from '@mui/material/Dialog';
import CloseIcon      from '@mui/icons-material/Close';
import CheckIcon      from '@mui/icons-material/Check';
import AddIcon         from '@mui/icons-material/Add';
import ChevronLeftIcon  from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { ShortlistToggleButton } from './ShortlistShared3';
import { apiFetch } from '../../../api/apiClient';
import { formatMaterialPrice, formatFurniturePrice } from '../../../shared/formatPrice';
import { LoadingState, ErrorState, InlineAlert, useToast } from '../../../shared/components/status';

import {
  GREEN, GREEN50, CHARCOAL, BORDER, MUTED, MUTED_FAINT, STONE50, WARNING,
  labelMono, bodyFont, appGridBg,
} from '../../../theme/tokens2';

// ─── Two ways to open the Lightbox ───────────────────────────────────────────
// 1. From the Moodboard page (normal): every photo in the room, every marker,
//    the customer taps markers to see options.
// 2. From a shortlist card's "See in photo" link (focused): ONE photo and ONE
//    marker — the spot the clicked item came from — already selected. The
//    item is pinned at the top of the options panel with a status strip, and
//    closing returns the customer to exactly where they were.
//    Turned on by passing `focus`. See the Lightbox props further down.

// The soft green circle that grows out from the source marker and fades.
// It starts at 24px and grows to about 60px.
// It plays 3 times when the pop-up opens, then stops.
const pulse = keyframes`
  0%   { transform: translate(-50%, -50%) scale(1);   opacity: 0.6; }
  75%  { transform: translate(-50%, -50%) scale(2.5); opacity: 0;   }
  100% { transform: translate(-50%, -50%) scale(2.5); opacity: 0;   }
`;

// ─── CandidateCard ────────────────────────────────────────────────────────────
// Renders different details depending on candidate.type ("material" | "furniture").
// Compact horizontal layout: thumbnail + text, so name, price, vendor and the
// Save toggle stay visible in the narrow side panel.
// Vendor · price · type line shared by both kinds of card.
function metaFor(candidate) {
  const isMaterial  = candidate.type === 'material';
  const isFurniture = candidate.type === 'furniture';
  return [
    candidate.vendor,
    // Materials: free text ("SGD 4.60 / sqft"). Furniture: a number ("SGD 1299.50").
    // Left out (not "—") when empty, because this line already skips empty parts.
    candidate.price == null || String(candidate.price).trim() === ''
      ? null
      : isMaterial
        ? formatMaterialPrice(candidate.price)
        : formatFurniturePrice(candidate.price),
    isMaterial  ? candidate.cat_materials : null,
    isFurniture ? candidate.tab          : null,
    isFurniture ? candidate.subcategory  : null,
  ].filter(Boolean);
}

const shortlistNameFor = (candidate) => (candidate.type === 'material' ? 'Materials' : 'Furniture');

function CandidateCard({ candidate, isSaved, onToggleSave }) {
  const isMaterial  = candidate.type === 'material';
  const shortlistName = shortlistNameFor(candidate);
  const metaParts = metaFor(candidate);

  return (
    <Box sx={{
      border: isSaved ? `1.5px solid ${GREEN}` : `0.5px solid ${BORDER}`,
      borderRadius: '3px',
      overflow: 'hidden',
      background: '#fff',
      flexShrink: 0,
    }}>
      <Box sx={{ display: 'flex', gap: '14px', p: '12px', alignItems: 'center' }}>
        {candidate.imagesrc && (
          <Box
            component="img"
            src={candidate.imagesrc}
            alt={candidate.namelabel || 'Option'}
            sx={{
              width: 120, height: 120, objectFit: 'cover',
              flexShrink: 0, borderRadius: '3px',
              border: `0.5px solid ${BORDER}`,
            }}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: '12px', fontWeight: 500, color: CHARCOAL, lineHeight: 1.3,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden', textOverflow: 'ellipsis',
            ...bodyFont,
          }}>
            {candidate.namelabel || 'Untitled'}
          </Typography>

          {metaParts.length > 0 && (
            <Typography sx={{ ...labelMono, fontSize: '8px', color: MUTED, mt: '3px' }}>
              {metaParts.join(' · ')}
            </Typography>
          )}

          {isMaterial && candidate.dimensions && (
            <Typography sx={{ ...labelMono, fontSize: '8px', color: MUTED_FAINT, mt: '2px' }}>
              {candidate.dimensions}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Save / undo — click again while saved to remove from the shortlist */}
      <ShortlistToggleButton
        variant="solid"
        isMarked={isSaved}
        onToggle={() => onToggleSave(candidate)}
        unmarkedLabel={`Save to ${shortlistName} Shortlist`}
        markedLabel={`✓ Saved to ${shortlistName} Shortlist`}
        removeLabel={`Remove from ${shortlistName} Shortlist`}
      />
    </Box>
  );
}

// ─── PinnedCandidateCard ──────────────────────────────────────────────────────
// Focused mode only: the item the customer clicked "See in photo" on.
// Stands out from the other options (green border, pale green background,
// bigger photo) and has a status strip across the top:
//   saved   → green: "✓ On your Materials shortlist · Tiles #02"
//   removed → brown: "Removed · it leaves your shortlist when you close"
// The strip is announced to screen readers when it changes.
function PinnedCandidateCard({ candidate, focus, onToggleSave }) {
  const shortlistName = shortlistNameFor(candidate);
  const metaParts = metaFor(candidate);
  const saved = candidate.in_shortlist;

  const strip = saved
    ? { bg: GREEN, text: `✓ On your ${focus.shortlistName} shortlist${focus.rankLabel ? ` · ${focus.rankLabel}` : ''}` }
    : {
        bg: WARNING,
        text: candidate.left_samples
          ? 'Removed · it leaves your shortlist and samples request when you close'
          : 'Removed · it leaves your shortlist when you close',
      };

  return (
    <Box sx={{
      border: `2px solid ${saved ? GREEN : WARNING}`,
      borderRadius: '3px',
      overflow: 'hidden',
      background: GREEN50,
      flexShrink: 0,
      transition: 'border-color 0.2s ease',
    }}>
      <Box
        role="status"
        aria-live="polite"
        sx={{
          background: strip.bg, color: '#fff',
          px: '12px', py: '7px',
          fontSize: '10px', fontWeight: 600, lineHeight: 1.4,
          transition: 'background-color 0.2s ease',
          ...bodyFont,
        }}
      >
        {strip.text}
      </Box>

      <Box sx={{ display: 'flex', gap: '14px', p: '12px', alignItems: 'center' }}>
        {candidate.imagesrc && (
          <Box
            component="img"
            src={candidate.imagesrc}
            alt={candidate.namelabel || 'Your item'}
            sx={{
              width: 104, height: 104, objectFit: 'cover',
              flexShrink: 0, borderRadius: '3px',
              border: `0.5px solid ${BORDER}`,
            }}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: '13px', fontWeight: 600, color: CHARCOAL, lineHeight: 1.3,
            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
            overflow: 'hidden', ...bodyFont,
          }}>
            {candidate.namelabel || 'Untitled'}
          </Typography>
          {metaParts.length > 0 && (
            <Typography sx={{ ...labelMono, fontSize: '8px', color: MUTED, mt: '4px' }}>
              {metaParts.join(' · ')}
            </Typography>
          )}
          {candidate.type === 'material' && candidate.dimensions && (
            <Typography sx={{ ...labelMono, fontSize: '8px', color: MUTED_FAINT, mt: '2px' }}>
              {candidate.dimensions}
            </Typography>
          )}
        </Box>
      </Box>

      <ShortlistToggleButton
        variant="solid"
        isMarked={saved}
        onToggle={() => onToggleSave(candidate)}
        unmarkedLabel={`Save to ${shortlistName} Shortlist`}
        markedLabel={`✓ Saved to ${shortlistName} Shortlist`}
        removeLabel={`Remove from ${shortlistName} Shortlist`}
      />
    </Box>
  );
}

// Small green caps label used above the pinned card and the other options.
function PanelLabel({ children, divider = false }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: divider ? '6px' : 0 }}>
      <Typography component="h3" sx={{ ...labelMono, fontSize: '8px', color: divider ? MUTED : GREEN, whiteSpace: 'nowrap', m: 0 }}>
        {children}
      </Typography>
      {divider && <Box sx={{ flex: 1, height: '0.5px', background: BORDER }} />}
    </Box>
  );
}

// ─── CandidatesPanel ──────────────────────────────────────────────────────────
function CandidatesPanel({ activeRegionId, activeRegionType, candidates, onToggleSave, status, onRetry, focus }) {
  const typeLabel = activeRegionType === 'material' ? 'Material' : 'Furniture';

  // Focused mode: pull the clicked item out of the list so it can be pinned.
  // If it isn't among the options (e.g. QC has since rejected it), the
  // normal list is shown instead.
  const pinned = focus ? candidates.find((c) => c.id === focus.itemId) : null;
  const others = pinned ? candidates.filter((c) => c !== pinned) : candidates;
  return (
    <Box sx={{
      width: 320, flexShrink: 0,
      backgroundColor: '#fff',
      borderLeft: `0.5px solid ${BORDER}`,
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Empty state — no region selected */}
      {!activeRegionId && (
        <Box sx={{
          flex: 1, display: 'flex', alignItems: 'center',
          justifyContent: 'center', px: 3,
        }}>
          <Typography sx={{
            ...labelMono, fontSize: '9px', color: MUTED_FAINT,
            textAlign: 'center', lineHeight: 1.6,
          }}>
            Tap a marker on the photo to see matching options
          </Typography>
        </Box>
      )}

      {activeRegionId && status === 'loading' && (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LoadingState label="Loading options…" />
        </Box>
      )}

      {activeRegionId && status === 'error' && (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ErrorState
            title="We couldn't load these options"
            message="Please check your connection and try again."
            onRetry={onRetry}
          />
        </Box>
      )}

      {activeRegionId && status === 'ready' && (
        <>
          {/* Panel header */}
          <Box sx={{
            px: 2, py: '8px',
            borderBottom: `0.5px solid ${BORDER}`,
            backgroundColor: STONE50,
            flexShrink: 0,
          }}>
            <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED }}>
              {focus
                ? `${candidates.length} option${candidates.length !== 1 ? 's' : ''} found`
                : `${candidates.length} ${typeLabel} Option${candidates.length !== 1 ? 's' : ''}`}
            </Typography>
          </Box>

          {/* List */}
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {pinned ? (
              <>
                <PanelLabel>The item you clicked</PanelLabel>
                <PinnedCandidateCard candidate={pinned} focus={focus} onToggleSave={onToggleSave} />
                {others.length > 0 && (
                  <PanelLabel divider>Other options for this spot ({others.length})</PanelLabel>
                )}
                {others.map((candidate) => (
                  <CandidateCard
                    key={`${candidate.type}-${candidate.id}`}
                    candidate={candidate}
                    isSaved={candidate.in_shortlist}
                    onToggleSave={onToggleSave}
                  />
                ))}
              </>
            ) : candidates.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography sx={{
                  ...labelMono, fontSize: '9px', color: MUTED_FAINT,
                  textAlign: 'center', lineHeight: 1.6,
                }}>
                  No options found for this spot yet.
                </Typography>
              </Box>
            ) : (
              candidates.map((candidate) => (
                <CandidateCard
                  key={`${candidate.type}-${candidate.id}`}
                  candidate={candidate}
                  isSaved={candidate.in_shortlist}
                  onToggleSave={onToggleSave}
                />
              ))
            )}
          </Box>

          {/* Footer hint */}
          <Box sx={{
            px: '10px', py: '10px',
            borderTop: `0.5px solid ${BORDER}`,
            background: 'rgba(33,131,51,0.04)',
            borderLeft: '2px solid rgba(33,131,51,0.35)',
            flexShrink: 0,
          }}>
            <Typography sx={{ fontSize: '10px', color: MUTED, lineHeight: 1.55, ...bodyFont }}>
              {focus
                ? `Saved items appear in your ${focus.shortlistName} shortlist. Tap Saved again to undo.`
                : 'Tap another marker to switch. Saved items appear in their shortlist — tap Saved again to undo.'}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
}

// ─── RegionMarker ─────────────────────────────────────────────────────────────
// A dot in the middle of each spot we found options for.
// Shape shows the type: circle = material, square = furniture.

function RegionMarker({ region, isActive, onClick }) {
  const [hovered, setHovered] = useState(false);
  const isMaterial = region.region_type === 'material';

  // The marker sits at the centre of the region's box.
  const cx = region.x_pct + region.width_pct / 2;
  const cy = region.y_pct + region.height_pct / 2;

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onClick(region.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={`${isMaterial ? 'Material' : 'Furniture'} options`}
      aria-pressed={isActive}
      sx={{
        position: 'absolute',
        left: `${cx}%`,
        top: `${cy}%`,
        transform: `translate(-50%, -50%) scale(${isActive ? 1.1 : hovered ? 1.06 : 1})`,
        width: 20, height: 20,
        p: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: isMaterial ? '50%' : '3px',
        background: isActive
          ? GREEN
          : hovered
            ? 'rgba(33,131,51,0.18)'
            : 'rgba(255,255,255,0.72)',
        backdropFilter: 'blur(6px) saturate(160%)',
        WebkitBackdropFilter: 'blur(6px) saturate(160%)',
        border: `2.5px solid ${isActive ? GREEN : 'rgba(33,131,51,0.55)'}`,
        outline: '2px solid rgba(255,255,255,0.85)',
        outlineOffset: '-1px',
        boxShadow: isActive
          ? '0 3px 10px rgba(33,131,51,0.4), 0 1px 2px rgba(0,0,0,0.15)'
          : '0 2px 5px rgba(0,0,0,0.15), 0 0.5px 1px rgba(0,0,0,0.08)',
        cursor: 'pointer',
        transition: 'transform 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, backdrop-filter 0.15s ease',
        zIndex: isActive ? 2 : 1,
        '&:focus-visible': { outline: `3px solid ${CHARCOAL}`, outlineOffset: '2px' },
      }}
    >
      <AddIcon sx={{ fontSize: '14px', color: isActive ? '#fff' : GREEN, pointerEvents: 'none' }} />
    </Box>
  );
}

// ─── SourceMarker ─────────────────────────────────────────────────────────────
// Focused mode only: the one spot the clicked item came from. Not clickable
// (it's already selected). Looks exactly like a selected RegionMarker, so the
// customer sees the same "selected" look in both modes. A soft pulse plays
// 3 times when the pop-up opens to draw the eye to it.
// If the RegionMarker's selected look changes, change this to match.
function SourceMarker({ region }) {
  const isMaterial = region.region_type === 'material';
  const cx = region.x_pct + region.width_pct / 2;
  const cy = region.y_pct + region.height_pct / 2;
  const centred = { position: 'absolute', left: `${cx}%`, top: `${cy}%`, pointerEvents: 'none' };

  return (
    <>
      {/* Pulse: grows and fades, 3 times only. Skipped for people who ask for less motion. */}
      <Box aria-hidden sx={{
        ...centred,
        transform: 'translate(-50%, -50%)',
        width: 24, height: 24, borderRadius: '50%',
        background: 'rgba(33,131,51,0.45)',
        opacity: 0,
        animation: `${pulse} 1.6s ease-out 0.35s 3`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        zIndex: 1,
      }} />

      {/* The marker itself: same look as a selected RegionMarker */}
      <Box
        role="img"
        aria-label="Where your item comes from"
        sx={{
          ...centred,
          transform: 'translate(-50%, -50%) scale(1.1)',
          width: 20, height: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: isMaterial ? '50%' : '3px',
          background: GREEN,
          border: `2.5px solid ${GREEN}`,
          outline: '2px solid rgba(255,255,255,0.85)',
          outlineOffset: '-1px',
          boxShadow: '0 3px 10px rgba(33,131,51,0.4), 0 1px 2px rgba(0,0,0,0.15)',
          zIndex: 2,
        }}
      >
        <AddIcon sx={{ fontSize: '14px', color: '#fff' }} />
      </Box>
    </>
  );
}

// ─── TypeToggle ───────────────────────────────────────────────────────────────
// A shape-shaped checkbox: doubles as the legend explaining what each marker
// shape means and as a show/hide filter. Disabled when this photo has no
// markers of that type at all.
function TypeToggle({ shape, label, checked, onClick, disabled }) {
  const isCircle = shape === 'circle';
  const active = checked && !disabled;
  return (
    <Box
      component="button"
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-pressed={active}
      sx={{
        display: 'flex', alignItems: 'center', gap: '6px',
        background: 'none', border: 'none', p: 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
      }}
    >
      <Box sx={{
        width: 14, height: 14,
        borderRadius: isCircle ? '50%' : '3px',
        background: '#fff',
        border: `1.5px solid ${active ? GREEN : MUTED_FAINT}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'border-color 0.15s ease',
        flexShrink: 0,
      }}>
        {active && <CheckIcon sx={{ fontSize: '10px', color: GREEN }} />}
      </Box>
      <Typography sx={{ ...labelMono, fontSize: '8px', color: disabled ? MUTED_FAINT : (checked ? CHARCOAL : MUTED_FAINT) }}>
        {label}{disabled ? ' — none found' : ''}
      </Typography>
    </Box>
  );
}

// ─── PhotoArea ────────────────────────────────────────────────────────────────
function PhotoArea({
  photo, regions, regionsError, onRetryRegions, activeRegionId, visibleTypes,
  onRegionClick, onPrev, onNext, onToggleType,
  hasPrev, hasNext,
  sourceRegionId = null, // focused mode: show only this spot, as a SourceMarker
}) {
  const focused = sourceRegionId != null;
  const [naturalAspect, setNaturalAspect] = useState(null);

  const handleImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    if (naturalHeight > 0) setNaturalAspect(naturalWidth / naturalHeight);
  };

  return (
    <Box sx={{
      flex: 1, position: 'relative', overflow: 'hidden',
      ...appGridBg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      p: '28px',
      boxSizing: 'border-box',
    }}>
      {/*
        Two boxes with one job each:
        - this one just fills the stage and centers whatever's inside it
        - the next one is cut to the exact shape of the photo, so the
          markers' % positions land on the real photo, not on blank space
        Markers wait for naturalAspect (the photo's real shape) before
        drawing, so they don't briefly appear in the wrong spot.
      */}
      <Box sx={{
        position: 'relative',
        width: '100%', height: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Box sx={{
          position: 'relative',
          border: `0.5px solid ${BORDER}`,
          borderRadius: '3px',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          background: '#fff',
          ...(naturalAspect
            ? { aspectRatio: `${naturalAspect}`, maxWidth: '100%', maxHeight: '100%',
                width: 'auto', height: 'auto', flex: '0 1 auto' }
            : { width: '100%', height: '100%' }),
        }}>
          <Box
            component="img"
            src={photo.imagesrc}
            alt={`${photo.cat_room || 'Room'} photo`}
            onLoad={handleImageLoad}
            sx={{
              display: 'block', width: '100%', height: '100%',
              objectFit: naturalAspect ? 'fill' : 'contain',
            }}
          />

          {naturalAspect && focused && regions
            .filter((region) => region.id === sourceRegionId)
            .map((region) => <SourceMarker key={region.id} region={region} />)}

          {naturalAspect && !focused && regions
            .filter((region) => visibleTypes[region.region_type === 'material' ? 'material' : 'furniture'])
            .map((region) => (
              <RegionMarker
                key={region.id}
                region={region}
                isActive={activeRegionId === region.id}
                onClick={onRegionClick}
              />
            ))}
        </Box>
      </Box>

      {/* Couldn't load the markers: say so on the photo, with a retry */}
      {regionsError && (
        <Box sx={{ position: 'absolute', top: 14, left: '50%', transform: 'translateX(-50%)', zIndex: 3, maxWidth: 420 }}>
          <InlineAlert severity="error">
            We couldn't load the markers for this photo.{' '}
            <Box
              component="button"
              type="button"
              onClick={onRetryRegions}
              sx={{ background: 'none', border: 'none', p: 0, color: 'inherit', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
            >
              Try again
            </Box>
          </InlineAlert>
        </Box>
      )}

      {/* Prev arrow */}
      {hasPrev && (
        <Box
          component="button"
          type="button"
          onClick={onPrev}
          aria-label="Previous photo"
          sx={{
            position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
            width: 32, height: 32, borderRadius: '50%',
            background: '#fff',
            border: `0.5px solid ${BORDER}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.10)',
            cursor: 'pointer', color: CHARCOAL,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'border-color 0.15s, color 0.15s',
            '&:hover': { borderColor: GREEN, color: GREEN },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: '1.1rem' }} />
        </Box>
      )}

      {/* Next arrow */}
      {hasNext && (
        <Box
          component="button"
          type="button"
          onClick={onNext}
          aria-label="Next photo"
          sx={{
            position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
            width: 32, height: 32, borderRadius: '50%',
            background: '#fff',
            border: `0.5px solid ${BORDER}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.10)',
            cursor: 'pointer', color: CHARCOAL,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'border-color 0.15s, color 0.15s',
            '&:hover': { borderColor: GREEN, color: GREEN },
          }}
        >
          <ChevronRightIcon sx={{ fontSize: '1.1rem' }} />
        </Box>
      )}

      {/* Legend + show/hide filter — bottom-left (not needed with one marker) */}
      {!focused && <Box sx={{
        position: 'absolute', bottom: 18, left: 18,
        px: '12px', py: '8px', borderRadius: '6px',
        background: '#fff',
        border: `0.5px solid ${BORDER}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.10)',
      }}>
        <Typography sx={{ ...labelMono, fontSize: '7px', color: MUTED_FAINT, mb: '6px', display: 'block' }}>
          Tap to show/hide markers
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <TypeToggle
            shape="circle" label="Materials"
            checked={visibleTypes.material}
            onClick={() => onToggleType('material')}
            disabled={!regions.some((r) => r.region_type === 'material')}
          />
          <TypeToggle
            shape="square" label="Furniture"
            checked={visibleTypes.furniture}
            onClick={() => onToggleType('furniture')}
            disabled={!regions.some((r) => r.region_type !== 'material')}
          />
        </Box>
      </Box>}
    </Box>
  );
}

// ─── Lightbox ─────────────────────────────────────────────────────────────────
// Props:
//   photos            — photo items to page through (Moodboard: the whole room;
//                       "See in photo": just the one photo)
//   initialPhotoIndex — which photo to open first
//   selectedCategory  — room name (for the title)
//   onClose           — () => void
//
// Optional, for the "See in photo" pop-up (leave them out on the Moodboard):
//   focus               — { regionId, itemId, rankLabel, shortlistName }
//                         e.g. { regionId: 42, itemId: 1187, rankLabel: 'Tiles #02',
//                                shortlistName: 'Materials' }
//   returnLabel         — 'Materials' → the header button reads "← Back to Materials"
//   onClosedWithChanges — (changes) => void. Called on close if anything was
//                         saved or removed. changes = { any: true,
//                         removed: [ids taken off the shortlist],
//                         removedFromSamples: [{ id, name }] }
export default function Lightbox({
  photos = [],
  initialPhotoIndex = 0,
  selectedCategory = '',
  onClose,
  focus = null,
  returnLabel = null,
  onClosedWithChanges,
}) {
  const toast = useToast();

  // Focused mode needs to know which spot to show. Without one (an older item
  // with no spot recorded), the photo opens normally with all its markers.
  const isFocused = !!(focus && focus.regionId != null);
  const focusRegionId = isFocused ? focus.regionId : null;

  const missingRegion = !!focus && focus.regionId == null;
  useEffect(() => {
    if (missingRegion) {
      console.warn('[Lightbox] "See in photo" item has no region_id; opening the photo without a selected spot.');
    }
  }, [missingRegion]);

  const [currentIndex,     setCurrentIndex]     = useState(initialPhotoIndex);
  const [visibleTypes,     setVisibleTypes]     = useState({ material: true, furniture: true });
  const [regions,          setRegions]          = useState([]);
  const [regionsError,     setRegionsError]     = useState(false);
  const [activeRegionId,   setActiveRegionId]   = useState(focusRegionId);
  const [candidates,       setCandidates]       = useState([]);
  const [candidatesStatus, setCandidatesStatus] = useState('idle'); // 'idle' | 'loading' | 'ready' | 'error'

  // What the customer saved or removed while the pop-up was open, so the
  // page underneath can refresh when it closes.
  const changesRef = useRef({ any: false, removed: [], removedFromSamples: [] });

  const currentPhoto = photos[currentIndex];

  // Markers for the current photo
  const fetchRegions = useCallback(async (sceneId) => {
    setRegionsError(false);
    try {
      setRegions((await apiFetch(`/api/scenes/${sceneId}/regions`)) || []);
    } catch (err) {
      setRegionsError(true);
    }
  }, []);

  useEffect(() => {
    if (!currentPhoto?.id) return;
    setRegions([]);
    setActiveRegionId(focusRegionId); // focused: the item's spot is selected straight away
    setCandidates([]);
    fetchRegions(currentPhoto.id);
  }, [currentPhoto?.id, fetchRegions, focusRegionId]);

  // Options for the selected marker
  const fetchCandidates = useCallback(async (regionId) => {
    setCandidatesStatus('loading');
    try {
      setCandidates((await apiFetch(`/api/regions/${regionId}/candidates`)) || []);
      setCandidatesStatus('ready');
    } catch (err) {
      setCandidates([]);
      setCandidatesStatus('error');
    }
  }, []);

  useEffect(() => {
    if (!activeRegionId) { setCandidates([]); setCandidatesStatus('idle'); return; }
    fetchCandidates(activeRegionId);
  }, [activeRegionId, fetchCandidates]);

  // Save or unsave an option. The server flips whatever is stored and
  // tells us the new values. Unsaving a material also takes it out of the
  // samples request (the server does that); `left_samples` remembers it so
  // the pinned card and the notice after closing can say so.
  const handleToggleSave = async (candidate) => {
    const path = candidate.type === 'material'
      ? `/api/candidates/material/${candidate.id}/toggle-shortlist`
      : `/api/candidates/furniture/${candidate.id}/toggle-shortlist`;
    try {
      const { in_shortlist, in_samples_request = false } = await apiFetch(path, { method: 'PUT' });
      const leftSamples = !in_shortlist && !!candidate.in_samples_request;

      setCandidates((prev) => prev.map((c) =>
        (c.type === candidate.type && c.id === candidate.id)
          ? { ...c, in_shortlist, in_samples_request, left_samples: leftSamples }
          : c
      ));

      // Pressing again undoes, so each list is "latest state wins".
      const changes = changesRef.current;
      changes.any = true;
      changes.removed = changes.removed.filter((id) => id !== candidate.id);
      changes.removedFromSamples = changes.removedFromSamples.filter((r) => r.id !== candidate.id);
      if (!in_shortlist) changes.removed.push(candidate.id);
      if (leftSamples) {
        changes.removedFromSamples.push({ id: candidate.id, name: candidate.namelabel || 'This item' });
      }
    } catch (err) {
      toast.error(candidate.in_shortlist
        ? "We couldn't remove this from your shortlist. Please try again."
        : "We couldn't save this to your shortlist. Please try again.");
    }
  };

  const handleClose = () => {
    if (changesRef.current.any) onClosedWithChanges?.(changesRef.current);
    onClose();
  };

  // Clicking the active marker again deselects it (not in focused mode,
  // where the one marker is already selected and can't be clicked).
  const handleRegionClick = (id) => {
    if (isFocused) return;
    setActiveRegionId((prev) => (prev === id ? null : id));
  };

  // Keyboard: left/right arrows switch photos (Esc is handled by the Dialog).
  // Focused mode shows one photo only.
  useEffect(() => {
    if (isFocused) return undefined;
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft')  setCurrentIndex((i) => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setCurrentIndex((i) => Math.min(photos.length - 1, i + 1));
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [photos.length, isFocused]);

  if (!currentPhoto) return null;

  const ranking = currentPhoto.ranking ?? currentIndex + 1;

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth={false}
      aria-labelledby="lightbox-title"
      PaperProps={{
        sx: {
          width: '95vw', height: '90vh',
          maxWidth: '95vw', maxHeight: '90vh',
          m: 0, borderRadius: '3px',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
        px: 2.5, pt: returnLabel ? 1.5 : 2, pb: 1.5,
        borderBottom: `0.5px solid ${BORDER}`,
        flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', minWidth: 0 }}>
          <Typography id="lightbox-title" sx={{ ...labelMono, fontSize: '10px', color: CHARCOAL, whiteSpace: 'nowrap' }}>
            {selectedCategory} · photo {String(ranking).padStart(2, '0')}
          </Typography>
          {returnLabel && (
            <Typography sx={{ fontSize: '11px', fontStyle: 'italic', color: MUTED_FAINT, whiteSpace: 'nowrap', ...bodyFont }}>
              from your moodboard
            </Typography>
          )}
        </Box>

        {returnLabel ? (
          // A labelled button, so the customer knows they haven't left their page.
          <Box
            component="button"
            type="button"
            onClick={handleClose}
            sx={{
              ...labelMono, fontSize: '9px', color: CHARCOAL,
              minHeight: 40, px: '14px',
              background: '#fff', border: `0.5px solid ${BORDER}`, borderRadius: '3px',
              cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
              transition: 'border-color 0.15s, color 0.15s',
              '&:hover': { borderColor: GREEN, color: GREEN },
              '&:focus-visible': { outline: `2px solid ${GREEN}`, outlineOffset: '2px' },
            }}
          >
            ← Back to {returnLabel}
          </Box>
        ) : (
          <Box
            component="button"
            type="button"
            onClick={handleClose}
            aria-label="Close"
            sx={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: MUTED, display: 'flex', p: '4px',
              '&:hover': { color: CHARCOAL },
            }}
          >
            <CloseIcon sx={{ fontSize: '1rem' }} />
          </Box>
        )}
      </Box>

      {/* ── Body — photo + options ── */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <PhotoArea
          photo={currentPhoto}
          regions={regions}
          regionsError={regionsError}
          onRetryRegions={() => fetchRegions(currentPhoto.id)}
          activeRegionId={activeRegionId}
          visibleTypes={visibleTypes}
          onRegionClick={handleRegionClick}
          onPrev={() => { setCurrentIndex((i) => Math.max(0, i - 1)); setActiveRegionId(null); }}
          onNext={() => { setCurrentIndex((i) => Math.min(photos.length - 1, i + 1)); setActiveRegionId(null); }}
          onToggleType={(type) => setVisibleTypes((prev) => ({ ...prev, [type]: !prev[type] }))}
          hasPrev={!isFocused && currentIndex > 0}
          hasNext={!isFocused && currentIndex < photos.length - 1}
          sourceRegionId={focusRegionId}
        />

        <CandidatesPanel
          activeRegionId={activeRegionId}
          activeRegionType={regions.find((r) => r.id === activeRegionId)?.region_type}
          candidates={candidates}
          onToggleSave={handleToggleSave}
          status={candidatesStatus}
          onRetry={() => fetchCandidates(activeRegionId)}
          focus={isFocused ? focus : null}
        />
      </Box>
    </Dialog>
  );
}
